/*!
VueLayers
Vue 2 components to work with OpenLayers

@package vuelayers
@author Vladimir Vershinin <ghettovoice@gmail.com>
@version 0.7.5
@license MIT
@copyright (c) 2017, Vladimir Vershinin <ghettovoice@gmail.com>
*/
'use strict';

function _interopDefault (ex) { return (ex && (typeof ex === 'object') && 'default' in ex) ? ex['default'] : ex; }

var _Object$defineProperties = _interopDefault(require('babel-runtime/core-js/object/define-properties'));
var _Promise = _interopDefault(require('babel-runtime/core-js/promise'));
var _forEach = _interopDefault(require('lodash/fp/forEach'));
var _stubArray = _interopDefault(require('lodash/fp/stubArray'));
var _constant = _interopDefault(require('lodash/fp/constant'));
var _isFunction = _interopDefault(require('lodash/fp/isFunction'));
var _differenceWith = _interopDefault(require('lodash/fp/differenceWith'));
var _mapValues = _interopDefault(require('lodash/fp/mapValues'));
var __ol = _interopDefault(require('openlayers'));
var Vue = _interopDefault(require('vue'));
var core = require('../core');

// todo add other options, like event modifiers
var SelectInteraction = __ol.interaction.Select;
var Feature = __ol.Feature;
var props = {
  filter: {
    type: Function,
    default: _constant(true)
  },
  hitTolerance: {
    type: Number,
    default: 0
  },
  multi: {
    type: Boolean,
    default: false
  },
  /**
   * Initial selection
   * @type {string[]|number[]|GeoJSONFeature[]} Initial selection as Array of ids or Array of GeoJSON features
   */
  features: {
    type: Array,
    default: _stubArray
  },
  wrapX: {
    type: Boolean,
    default: true
  }
};

var computed = {};

var methods = {
  /**
   * @return {ol.interaction.Select}
   * @protected
   */
  createInteraction: function createInteraction() {
    return new SelectInteraction({
      multi: this.multi,
      wrapX: this.wrapX,
      filter: this.filter,
      style: this.createStyleFunc()
    });
  },

  /**
   * @return {ol.StyleFunction}
   * @protected
   */
  getDefaultStyles: function getDefaultStyles() {
    var defaultStyles = _mapValues(function (styles) {
      return styles.map(core.styleHelper.style);
    }, core.styleHelper.defaultEditStyle());

    return function __selectDefaultStyleFunc(feature) {
      if (feature.getGeometry()) {
        return defaultStyles[feature.getGeometry().getType()];
      }
    };
  },

  /**
   * @return {ol.Feature[]}
   */
  getFeatures: function getFeatures() {
    return this.$interaction && this.$interaction.getFeatures().getArray() || [];
  },

  /**
   * @returns {Object}
   * @protected
   */
  getServices: function getServices() {
    return core.mergeDescriptors(core.interaction.methods.getServices.call(this), core.stylesContainer.methods.getServices.call(this));
  },

  /**
   * @return {ol.interaction.Interaction|undefined}
   * @protected
   */
  getStyleTarget: function getStyleTarget() {
    return this.$interaction;
  },

  /**
   * @return {void}
   * @protected
   */
  mount: function mount() {
    core.interaction.methods.mount.call(this);
    this.features.forEach(this.select);
  },

  /**
   * @return {void}
   * @protected
   */
  unmount: function unmount() {
    this.unselectAll();
    core.interaction.methods.unmount.call(this);
  },

  /**
   * @param {GeoJSONFeature|Vue|ol.Feature|string|number} feature
   * @return {void}
   * @throws {Error}
   */
  select: function select(feature) {
    core.assert.hasMap(this);
    core.assert.hasInteraction(this);

    var id = core.featureHelper.getId(feature);
    if (!id) {
      throw new Error('Undefined feature id');
    }
    if (feature instanceof Vue) {
      feature = feature.$feature;
    }

    var selectedIds = this.$features.map(core.featureHelper.getId);
    if (selectedIds.includes(id)) return;

    if (!(feature instanceof Feature)) {
      feature = undefined;
      _forEach(function (layer) {
        var source = layer.getSource();

        if (source && _isFunction(source.getFeatureById)) {
          feature = source.getFeatureById(id);
        }

        return !feature;
      }, this.$map.getLayers().getArray());
    }

    feature && this.$interaction.getFeatures().push(feature);
  },

  /**
   * @param {GeoJSONFeature|Vue|ol.Feature|string|number} feature
   * @return {void}
   */
  unselect: function unselect(feature) {
    core.assert.hasInteraction(this);

    var id = core.featureHelper.getId(feature);
    if (!id) {
      throw new Error('Undefined feature id');
    }
    if (feature instanceof Vue) {
      feature = feature.$feature;
    }

    var selectedIds = this.$features.map(core.featureHelper.getId);
    var idx = selectedIds.findIndex(function (x) {
      return x === id;
    });

    if (idx !== -1) {
      this.$interaction.getFeatures().removeAt(idx);
    }
  },

  /**
   * @param {Array<{style: ol.style.Style, condition: (function|boolean|undefined)}>|ol.StyleFunction|Vue|undefined} styles
   * @return {void}
   * @protected
   */
  setStyle: function setStyle(styles) {
    if (styles !== this._styles) {
      this._styles = styles;
      this.refresh();
    }
  },

  /**
   * @return {Promise}
   */
  refresh: function refresh() {
    var _this = this;

    return _Promise.all([new _Promise(function (resolve) {
      if (_this.$interaction) {
        var featuresCollection = _this.$interaction.getFeatures();
        featuresCollection.once('change', function () {
          return resolve();
        });
        featuresCollection.changed();
      } else {
        resolve();
      }
    }), core.interaction.methods.refresh.call(this)]);
  },

  /**
   * @return {void}
   * @protected
   */
  subscribeAll: function subscribeAll() {
    subscribeToInteractionChanges.call(this);
  },

  /**
   * Removes all features from selection.
   * @return {void}
   */
  unselectAll: function unselectAll() {
    core.assert.hasInteraction(this);

    this.$interaction.getFeatures().clear();
  }
};

var diffById = _differenceWith(function (a, b) {
  return core.featureHelper.getId(a) === core.featureHelper.getId(b);
});
var watch = {
  features: function features(value) {
    if (!this.$interaction) return;

    var forSelect = diffById(value, this.$features);
    var forUnselect = diffById(this.$features, value);

    forSelect.forEach(this.select);
    forUnselect.forEach(this.unselect);
  }
};

var Interaction = { render: function render() {
    var _vm = this;var _h = _vm.$createElement;var _c = _vm._self._c || _h;return _c('i', { class: [_vm.$options.name], staticStyle: { "display": "none !important" } }, [_vm._t("default", null, { features: _vm.features })], 2);
  }, staticRenderFns: [],
  name: 'vl-interaction-select',
  mixins: [core.interaction, core.stylesContainer],
  props: props,
  computed: computed,
  methods: methods,
  watch: watch,
  stubVNode: {
    empty: false,
    attrs: function attrs() {
      return {
        class: this.$options.name
      };
    }
  },
  created: function created() {
    _Object$defineProperties(this, {
      $features: {
        enumerable: true,
        get: this.getFeatures
      }
    });
  }
};

/**
 * @return {void}
 * @private
 */
function subscribeToInteractionChanges() {
  var _this2 = this;

  core.assert.hasInteraction(this);

  var events = core.observableFromOlEvent(this.$interaction, 'select');

  this.subscribeTo(events, function (_ref) {
    var selected = _ref.selected,
        deselected = _ref.deselected,
        mapBrowserEvent = _ref.mapBrowserEvent;

    ++_this2.rev;

    deselected.forEach(function (feature) {
      return _this2.$emit('unselect', { feature: feature, mapBrowserEvent: mapBrowserEvent });
    });
    selected.forEach(function (feature) {
      return _this2.$emit('select', { feature: feature, mapBrowserEvent: mapBrowserEvent });
    });
    _this2.$emit('update:features', _this2.$features.map(function (f) {
      return core.geoJsonHelper.writeFeature(f, _this2.$view.getProjection());
    }));
  });
}

var index = {
  Interaction: Interaction,
  install: function install(Vue$$1) {
    Vue$$1.component(Interaction.name, Interaction);
  }
};

module.exports = index;

//# sourceMappingURL=index.js.map



//////////////////
// WEBPACK FOOTER
// ./~/vuelayers/lib/select-interaction/index.js
// module id = 1195
// module chunks = 1